import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import styles from './ArtifactPanel.module.css';
import { loadMonacoEditor } from 'components/loadMonacoEditor';

const contentTypeToLanguage = contentType => {
  switch (contentType) {
    case 'text/javascript':
    case 'text/typescript':
    case 'application/javascript':
    case 'application/vnd.aha.react':
    case 'application/vnd.aha.react_entrypoint':
      return 'typescript';

    case 'text/markdown':
      return 'markdown';

    case 'text/html':
      return 'html';

    default:
      return 'plaintext';
  }
};

const SourceView = ({ toolCall }) => {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editor = useRef<any>(); // We have to use 'any' here because Monaco is not imported yet.
  const lastContentRef = useRef<string>('');
  const isStreamingRef = useRef<boolean>(false);
  const userScrolledAwayRef = useRef<boolean>(false);
  const lastAutoScrollTimeRef = useRef<number>(0);

  // Parse modified_file from toolCall.content.data
  const toolCallData = toolCall.content?.data;
  const modifiedFile = useMemo(() => {
    if (toolCallData) {
      try {
        const data = JSON.parse(toolCallData) as {
          modified_file?: string;
        };
        return data.modified_file || null;
      } catch (e) {
        console.error('Failed to parse toolCall.content.data:', e);
      }
    }
    return null;
  }, [toolCallData]);

  // Determine the content to display
  const getEditorContent = useCallback((): string => {
    const body = toolCall.content.panel.body;

    // Try to parse as multi-file JSON
    try {
      const files = JSON.parse(body) as Record<string, string>;

      // If we have a modified_file and it exists, use that
      if (modifiedFile && files[modifiedFile]) {
        return files[modifiedFile];
      }

      // Otherwise default to App.tsx if it exists
      if (files['App.tsx']) {
        return files['App.tsx'];
      }

      // Otherwise use the first file
      const firstFile = Object.values(files)[0];
      if (firstFile) {
        return firstFile;
      }
    } catch (e) {
      // Not a multi-file artifact, treat as single file
    }

    // Fall back to treating body as a string
    return body;
  }, [modifiedFile, toolCall.content.panel.body]);

  const updateEditorContent = useCallback((content: string) => {
    if (!editor.current || lastContentRef.current === content) return;

    const model = editor.current.getModel();
    if (!model) return;

    const oldContent = lastContentRef.current;

    // Check if this is an append operation (common during streaming)
    if (content.startsWith(oldContent)) {
      // Only append the new content
      const newContent = content.substring(oldContent.length);
      if (newContent) {
        const lastLine = model.getLineCount();
        const lastColumn = model.getLineMaxColumn(lastLine);

        model.pushEditOperations(
          [],
          [
            {
              range: {
                startLineNumber: lastLine,
                startColumn: lastColumn,
                endLineNumber: lastLine,
                endColumn: lastColumn,
              },
              text: newContent,
            },
          ],
          () => null
        );
      }
    } else {
      // Full content replacement when content doesn't just append
      const fullRange = model.getFullModelRange();
      model.pushEditOperations(
        [],
        [
          {
            range: fullRange,
            text: content,
          },
        ],
        () => null
      );
    }

    // Auto-scroll to bottom during streaming (unless user scrolled away)
    if (isStreamingRef.current && !userScrolledAwayRef.current) {
      const lineCount = model.getLineCount();
      editor.current.revealLine(lineCount, 1); // 1 = ScrollType.Smooth
      lastAutoScrollTimeRef.current = Date.now();
    }

    lastContentRef.current = content;
  }, []);

  useEffect(() => {
    if (editorContainerRef.current) {
      const importEditor = async () => {
        // Dyanamically import the editor.
        const monaco = await loadMonacoEditor();

        // We got unmounted during the import.
        if (!editorContainerRef.current) return;

        // Mount the editor.
        const initialContent = getEditorContent();
        const newEditor = monaco.editor.create(editorContainerRef.current, {
          fixedOverflowWidgets: true,
          automaticLayout: true,
          lineNumbers: 'on',
          folding: false,
          wordWrap: 'on',
          wrappingIndent: 'deepIndent',
          tabSize: 2,
          insertSpaces: true,
          minimap: { enabled: false },
          value: initialContent,
          language: contentTypeToLanguage(toolCall.content.panel.contentType),
          theme: 'vs',
          readOnly: toolCall.status === 'preparing',
          scrollBeyondLastLine: false,
          smoothScrolling: true,
          padding: { bottom: 50 },
        });

        lastContentRef.current = initialContent;

        newEditor.onDidChangeModelContent(() => {
          if (!isStreamingRef.current) {
            toolCall.content.panel.body = newEditor.getValue();
            // TODO: Send changes to the server too. The simplest approach would
            // be to update this specific version of the artifact.
          }
        });

        // Detect when user scrolls to pause/resume auto-scrolling during streaming
        newEditor.onDidScrollChange(e => {
          if (!isStreamingRef.current) return;

          // If scroll position changed, check if we should pause/resume auto-scroll
          if (e.scrollTopChanged) {
            // Ignore scroll events that happened within 100ms of our auto-scroll
            // This prevents our own auto-scroll from being detected as user scroll
            const timeSinceAutoScroll =
              Date.now() - lastAutoScrollTimeRef.current;
            if (timeSinceAutoScroll < 100) return;

            const model = newEditor.getModel();
            if (!model) return;

            const visibleRanges = newEditor.getVisibleRanges();
            if (visibleRanges.length === 0) return;

            const lastLineNumber = model.getLineCount();
            const lastVisibleLine =
              visibleRanges[visibleRanges.length - 1].endLineNumber;

            // Consider "at bottom" if within 3 lines of the end
            const isAtBottom = lastLineNumber - lastVisibleLine <= 3;

            if (!isAtBottom) {
              // User scrolled away from bottom - pause immediately
              userScrolledAwayRef.current = true;
            } else if (userScrolledAwayRef.current) {
              // User scrolled back to bottom - resume auto-scroll
              userScrolledAwayRef.current = false;
            }
          }
        });

        newEditor.focus();
        editor.current = newEditor;
      };

      void importEditor();

      return () => {
        if (editor.current) {
          editor.current.dispose();
          editor.current = undefined;
        }
      };
    }
    // We don't need all the dependencies here since the editor is updated by the useEffect below when necessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorContainerRef, updateEditorContent]);

  // Handle streaming updates
  useEffect(() => {
    isStreamingRef.current = toolCall.status === 'preparing';

    if (editor.current) {
      const currentContent = getEditorContent();

      if (toolCall.status === 'preparing') {
        // Direct updates during streaming
        updateEditorContent(currentContent);
      } else if (toolCall.status === 'complete') {
        // Final update when streaming completes
        updateEditorContent(currentContent);

        if (editor.current.getRawOptions().readOnly) {
          editor.current.updateOptions({
            readOnly: false,
          });
        }
      }
    }
  }, [
    getEditorContent,
    toolCall.content.panel.body,
    toolCall.status,
    updateEditorContent,
  ]);

  if (!toolCall || !toolCall.content || !toolCall.content.panel) return null;

  return <div ref={editorContainerRef} className={styles.sourceView} />;
};

export default ApplicationView(SourceView);
